import { useCallback, useEffect, useState } from 'react';
import type { ImageScenario } from '../game/imageTypes';
import type { Tool } from '../game/partners';
import { allTools, isRuntimeTool, partnerById, toolById } from '../game/partners';
import { LEVELS, allLevels } from '../scenarios';
import { hiddenLevelIds, shadowedLevelIds } from '../game/contentStore';
import type { AuditEntry } from '../game/admin';
import {
  deleteLevel,
  downloadUsers,
  sendSignupEmailTest,
  sendSlackTest,
  fetchAdminContent,
  fetchAudit,
  removeContent,
  removeTool,
  restoreLevel,
  saveOrder,
} from '../game/admin';
import { BrandHeader } from './BrandHeader';
import { RTNav } from './RTNav';
import { LevelEditor } from './LevelEditor';
import { ToolEditor } from './ToolEditor';

interface Props {
  /** Reload the runtime content layer after a change, then re-render. */
  onContentChanged: () => Promise<void> | void;
  /**
   * The tool the admin was last looking at.
   *
   * Used as the default for a NEW level. Without it the editor always defaulted
   * to Jira, so adding a level while working in another tool filed it under
   * Jira unless the dropdown was noticed.
   */
  currentToolId?: string | undefined;
  onBack: () => void;
}

/**
 * The admin area.
 *
 * Reachable only from the admin menu entry, which only appears for a session
 * the SERVER confirmed is an admin. That confirmation is a convenience for the
 * UI; the routes behind every button check again, and a non-admin calling them
 * directly gets a 404.
 */
export function AdminMenu({ onContentChanged, currentToolId, onBack }: Props) {
  const [editing, setEditing] = useState<ImageScenario | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [creatingTool, setCreatingTool] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  const [runtimeIds, setRuntimeIds] = useState<Set<string>>(() => new Set());
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  /**
   * Which level is awaiting a delete confirmation.
   *
   * Two steps because a delete is live and irreversible for players, and a
   * built-in level can only be brought back by a deploy.
   */
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const content = await fetchAdminContent();
    if (content) {
      setOverrides(content.overrides);
      setRuntimeIds(new Set(content.levels.map((level) => level.id)));
    }
    setAudit(await fetchAudit());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const afterChange = useCallback(async () => {
    await onContentChanged();
    await refresh();
    setEditing(null);
    setCreating(false);
  }, [onContentChanged, refresh]);

  /**
   * Move a level within its own tool.
   *
   * Swapping with the neighbouring level OF THE SAME TOOL rather than the
   * adjacent row overall: the case numbers a player sees are per tool, so
   * moving past a level belonging to something else would look like nothing
   * happened.
   */
  const move = useCallback(
    async (levelId: string, direction: -1 | 1) => {
      const all = allLevels();
      const subject = all.find((level) => level.id === levelId);
      if (!subject) return;

      const siblings = all.filter((l) => l.source.toolId === subject.source.toolId);
      const at = siblings.findIndex((l) => l.id === levelId);
      const swapWith = siblings[at + direction];
      if (!swapWith) return;

      const ids = all.map((l) => l.id);
      const a = ids.indexOf(levelId);
      const b = ids.indexOf(swapWith.id);
      [ids[a], ids[b]] = [ids[b], ids[a]] as [string, string];

      const outcome = await saveOrder(ids);
      setMessage(outcome.message);
      await afterChange();
    },
    [afterChange],
  );

  const revert = useCallback(
    async (levelId: string) => {
      const outcome = await removeContent(levelId);
      setMessage(outcome.message);
      await afterChange();
    },
    [afterChange],
  );

  const compiledIds = new Set(LEVELS.map((level) => level.id));

  if (creatingTool || editingTool) {
    return (
      <ToolEditor
        existing={editingTool}
        onDone={() => {
          setCreatingTool(false);
          setEditingTool(null);
          void afterChange();
        }}
        onCancel={() => {
          setCreatingTool(false);
          setEditingTool(null);
        }}
      />
    );
  }

  if (creating) {
    return (
      <LevelEditor
        existing={null}
        {...(currentToolId ? { defaultToolId: currentToolId } : {})}
        onDone={() => void afterChange()}
        onCancel={() => setCreating(false)}
      />
    );
  }

  if (editing) {
    return (
      <LevelEditor
        existing={editing}
        isCompiled={compiledIds.has(editing.id)}
        onDone={() => void afterChange()}
        onCancel={() => setEditing(null)}
      />
    );
  }

  const levels = allLevels();

  return (
    <div className="panel">
      <RTNav />
      <div className="panel__inner">
        <BrandHeader />
        <p className="case-eyebrow">ReleaseTEAM only</p>
        <h1>Level admin</h1>

        <p className="admin-warning">
          Every change here is <b>live to players the moment it is saved</b>.
          There is no review step. The log at the bottom is the record of who
          changed what.
        </p>

        <button
          type="button"
          className="btn btn--wide"
          onClick={() => setCreating(true)}
          style={{ marginBottom: 18 }}
        >
          Add a level from a screenshot
        </button>

        {shadowedLevelIds(LEVELS).length > 0 && (
          <div className="admin-warning">
            <b>
              {shadowedLevelIds(LEVELS).length} saved level
              {shadowedLevelIds(LEVELS).length === 1 ? '' : 's'} cannot be shown.
            </b>{' '}
            Their id is already used by a level that ships in the build, so the
            built-in one wins and these are never rendered — to players or in the
            list below. The content is safe on the server, it is just
            unreachable under that id.
            <ul className="admin-problems">
              {shadowedLevelIds(LEVELS).map((id) => (
                <li key={id}>
                  <code>{id}</code>{' '}
                  <button
                    type="button"
                    className="btn btn--danger"
                    onClick={() => void revert(id)}
                  >
                    Delete it
                  </button>
                </li>
              ))}
            </ul>
            To keep the work, delete it here and save it again with a different
            id. To change the built-in level instead, use "Edit answers" on it.
          </div>
        )}

        <h2>Tool categories</h2>
        <p className="fineprint">
          A tool appears on the splash and the picker as soon as it exists, shown
          as "in preparation" until it has a level behind it.
        </p>
        {allTools().map((tool) => {
          const runtime = isRuntimeTool(tool.id);
          return (
            <div key={tool.id} className="admin-row">
              <div className="admin-row__body">
                <b style={{ color: partnerById(tool.partnerId).accent }}>{tool.short}</b>
                <span className="admin-row__meta">
                  {partnerById(tool.partnerId).name} · target {tool.targetLevels}
                  {runtime && <span className="admin-tag admin-tag--new">admin-added</span>}
                </span>
              </div>
              <div className="admin-row__actions">
                {runtime ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => setEditingTool(tool)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => {
                        void removeTool(tool.id).then(async (outcome) => {
                          setMessage(outcome.message);
                          await afterChange();
                        });
                      }}
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <span className="fineprint">ships in the build</span>
                )}
              </div>
            </div>
          );
        })}
        <button
          type="button"
          className="btn btn--ghost btn--wide"
          style={{ marginBottom: 18 }}
          onClick={() => setCreatingTool(true)}
        >
          Add a tool category
        </button>

        <h2>Levels</h2>
        <p className="fineprint">
          Levels are grouped by the tool they belong to, and numbered in this
          order for players. Change a level's tool from inside "Edit answers" —
          it moves to that tool's set and is renumbered there.
        </p>
        {levels.map((level, index) => {
          const siblings = levels.filter((l) => l.source.toolId === level.source.toolId);
          const positionInTool = siblings.findIndex((l) => l.id === level.id);
          /*
           * A heading whenever the tool CHANGES from the row above, not on the
           * first level of that tool. The list renders in global order, so once
           * levels are reordered across tools the same tool can appear in more
           * than one run — and keying on "first of its tool" put the later runs
           * silently under the previous tool's heading.
           */
          const previous = levels[index - 1];
          const firstOfTool = !previous || previous.source.toolId !== level.source.toolId;
          const isRuntime = runtimeIds.has(level.id) && !compiledIds.has(level.id);
          const isOverridden = Object.prototype.hasOwnProperty.call(overrides, level.id);
          return (
            <div key={level.id}>
              {firstOfTool && (
                <h3 className="admin-group">
                  <span
                    className="vendor__rule"
                    style={{ background: partnerById(toolById(level.source.toolId).partnerId).accent }}
                    aria-hidden="true"
                  />
                  {toolById(level.source.toolId).short}
                </h3>
              )}
              <div className="admin-row">
                <span className="admin-row__pos">{positionInTool + 1}</span>
              <div className="admin-row__body">
                <b>{level.title}</b>
                <span className="admin-row__meta">
                  {toolById(level.source.toolId).short} · {level.arrows.length} arrows ·{' '}
                  {level.defects.length} real
                  {isRuntime && <span className="admin-tag admin-tag--new">admin-added</span>}
                  {isOverridden && <span className="admin-tag admin-tag--edited">edited</span>}
                </span>
              </div>
              <div className="admin-row__actions">
                <button
                  type="button"
                  className="btn btn--ghost admin-move"
                  aria-label={`Move ${level.title} earlier`}
                  disabled={positionInTool === 0}
                  onClick={() => void move(level.id, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn--ghost admin-move"
                  aria-label={`Move ${level.title} later`}
                  disabled={positionInTool === siblings.length - 1}
                  onClick={() => void move(level.id, 1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setEditing(level)}
                >
                  Edit answers
                </button>
                {isOverridden && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => void revert(level.id)}
                  >
                    Revert edits
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn--danger"
                  onClick={() => setConfirmingDelete(level.id)}
                >
                  Delete
                </button>
              </div>
              </div>
            </div>
          );
        })}

        {confirmingDelete && (
          <div className="admin-warning">
            <b>Remove "{levels.find((l) => l.id === confirmingDelete)?.title}" from the game?</b>
            <br />
            {compiledIds.has(confirmingDelete)
              ? 'This level ships in the build, so it is hidden rather than deleted and you can put it back from the list below.'
              : 'This level was added here, so deleting it is permanent — the screenshot and every explanation go with it.'}{' '}
            Either way it disappears for players immediately, and scores earned on
            it stop counting towards the leaderboard.
            <div className="reset__actions" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => {
                  const id = confirmingDelete;
                  setConfirmingDelete(null);
                  void deleteLevel(id).then(async (outcome) => {
                    setMessage(outcome.message);
                    await afterChange();
                  });
                }}
              >
                Yes, remove it
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => setConfirmingDelete(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {hiddenLevelIds().length > 0 && (
          <>
            <h2>Removed from the game</h2>
            <p className="fineprint">
              These ship in the build, so they were hidden rather than deleted.
              Players do not see them and their scores do not count.
            </p>
            {hiddenLevelIds().map((id) => (
              <div key={id} className="admin-row">
                <div className="admin-row__body">
                  <b>{LEVELS.find((l) => l.id === id)?.title ?? id}</b>
                  <span className="admin-row__meta">{id}</span>
                </div>
                <div className="admin-row__actions">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => {
                      void restoreLevel(id).then(async (outcome) => {
                        setMessage(outcome.message);
                        await afterChange();
                      });
                    }}
                  >
                    Put it back
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {message && (
          <p className="form__message" role="status">
            {message}
          </p>
        )}

        <h2>Players</h2>
        <p className="admin-note">
          These files carry <b>real names, email addresses and consent state</b>.
          Most players have <b>not</b> opted into marketing — it is unticked by
          default and separate from playing — so check the consent column before
          anyone is contacted. Every download is recorded in the log below.
        </p>
        <div className="reset__actions" style={{ marginBottom: 10 }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              void downloadUsers('csv').then((o) => setMessage(o.message));
            }}
          >
            Download CSV
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              void downloadUsers('xlsx').then((o) => setMessage(o.message));
            }}
          >
            Download Excel
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              void sendSlackTest().then((o) => setMessage(o.message));
            }}
          >
            Send a Slack test
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              void sendSignupEmailTest().then((o) => setMessage(o.message));
            }}
          >
            Send a test sign-up email
          </button>
        </div>

        <h2>Change log</h2>
        {audit.length === 0 ? (
          <p className="fineprint">Nothing changed yet.</p>
        ) : (
          <div className="admin-audit">
            {audit.map((entry, index) => (
              <div key={`${entry.at}-${index}`} className="admin-audit__row">
                <span className="admin-audit__when">{entry.at.replace('T', ' ').slice(0, 16)}</span>
                <span className="admin-audit__who">{entry.actor}</span>
                <span className="admin-audit__what">
                  {entry.action} · {entry.target}
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          className="btn btn--ghost btn--wide"
          style={{ marginTop: 24 }}
          onClick={onBack}
        >
          Back to the game
        </button>
      </div>
    </div>
  );
}
